import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import {
  DialogTitle, DialogContent, Dialog, Box, Button, Stack, TextField, Typography, Card,
  CircularProgress, LinearProgress, Checkbox, Tooltip, DialogActions, Divider, CardContent
} from '@mui/material'
import { useSelector } from 'react-redux'
import { ClassNameMap, withStyles } from '@mui/styles'
import Draggable from "react-draggable"
import stylesheet from '../../detail/detail-container.stylesheet'
import { RootState } from '../../../../redux/store'
import DialogTitleHeaderComponent from '../common/dialog-title-header.component'
import CardLayoutComponent from '../common/card-layout.component'
import { PatientService } from '../../../../services/patient.service'
import { useUpdateDataAction } from '../../../../utils/update-details-hook'
import Helper from '../../../../utils/helper'
import { toast } from 'react-toastify'
import GradientCircleComponent from '../common/gradient-circle.component'

interface IProps {
  open: boolean,
  input: any,
  item: any[],
  handleClose: any,
  classes: ClassNameMap,
}

const SearchConditionsComponent: React.FC<IProps> = ({
  open,
  input,
  item,
  handleClose,
  classes,
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [recommendedConditions, setRecommendedConditions] = useState([]) as any
  const [addConditions, setAddConditions] = useState([]) as any
  const patientDetailStatus: any = useSelector((state: RootState) => state.patientDetail.status)
  const [isSubmit, setIsSubmit] = useState(false)
  const searchCalRef = useRef(null) as any
  const scrollableContainerRef = useRef(null) as any
  const searchInputRef = useRef<HTMLInputElement>(null)
  const debounceTimer = useRef<NodeJS.Timeout | null>(null)

  // Services
  const patientService = new PatientService()
  const { updatePatientData } = useUpdateDataAction(input?.patient_id)

  // Debounce effect for search input
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current)
    }

    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(search)
    }, 300)

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current)
      }
    }
  }, [search])

  // Cleanup timer on unmount
  useEffect(() => {
    setRecommendedConditions(input?.recommendedConditions || [])
    setAddConditions((input?.recommendedConditions)?.filter((i: any) => i.selected_for_evaluation))
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current)
      }
    }
  }, [])


  // function to replace the checkConditions function
  const hasDataChanged = useCallback(() => {
    if (!input?.recommendedConditions || !Array.isArray(input.recommendedConditions)) {
      return addConditions.length > 0
    }

    const changedConditions = []

    // Create maps for quick lookup
    const recommendedMap = new Map(
      input.recommendedConditions.map((rc: any) => [
        rc.condition_id,
        rc.selected_for_evaluation
      ])
    )

    const currentMap = new Map(
      addConditions.map((ac: any) => [
        ac.condition_id,
        ac.selected_for_evaluation
      ])
    )

    // Check for modified or added conditions
    for (const condition of addConditions) {
      const originalValue = recommendedMap.get(condition.condition_id)
      if (originalValue !== condition.selected_for_evaluation) {
        changedConditions.push(condition)
      }
    }

    // Check for removed conditions (originally selected but now unchecked)
    for (const originalCondition of input.recommendedConditions) {
      if (originalCondition.selected_for_evaluation === true) {
        const currentValue = currentMap.get(originalCondition.condition_id)
        // If it was selected before but now doesn't exist or is false
        if (currentValue === undefined || currentValue === false) {
          changedConditions.push({
            ...originalCondition,
            selected_for_evaluation: false
          })
        }
      }
    }

    return changedConditions.length > 0
  }, [addConditions, input?.recommendedConditions])

  // Create search index for faster lookups
  const searchIndex = useMemo(() => {
    if (!item || !Array.isArray(item)) return new Map()

    const index = new Map()
    item.forEach((obj, idx) => {
      const displayName = obj.condition_name || ''
      const searchText = `${displayName}`.toLowerCase().trim()

      if (searchText) {
        index.set(idx, {
          item: obj,
          searchText,
          displayName,
          index: idx
        })
      }
    })

    return index
  }, [item])

  const matchesAnyTerm = useCallback((text: string, searchTerms: string[]) => {
    if (!text) return false
    const lowerText = text.toLowerCase()
    return searchTerms.every(term => lowerText.includes(term))
  }, [])

  const getMatchScore = useCallback((obj: any, searchTerms: string[]) => {
    const displayName = (obj.condition_name || '').toLowerCase()
    const name = (obj.name || '').toLowerCase()

    let score = 0
    let matchedTerms = 0

    searchTerms.forEach(term => {
      if (displayName === term || name === term) {
        score += 100
        matchedTerms++
      }
      else if (displayName.startsWith(term) || name.startsWith(term)) {
        score += 50
        matchedTerms++
      }
      else if (displayName.includes(term) || name.includes(term)) {
        score += 10
        matchedTerms++
      }
    })

    score += matchedTerms * 5
    return { score, matchedTerms }
  }, [])

  const highlightMultipleTerms = useCallback((text: string, searchTerms: string[]) => {
    if (!text || searchTerms.length === 0) return text

    const escapedTerms = searchTerms.map(term =>
      term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    )
    const regex = new RegExp(`(${escapedTerms.join('|')})`, 'gi')
    const parts = text.split(regex)

    return parts.map((part, i) =>
      regex.test(part) ?
        <span key={i} style={{ backgroundColor: 'var(--color-eval-highlight-bg)' }}>{part}</span> :
        part
    )
  }, [])

  // Optimized search - only applied when search is active
  const filteredSearchResults = useMemo(() => {
    if (!debouncedSearch.trim()) return []
    if (debouncedSearch.trim().length < 3) return []

    const searchTerms = debouncedSearch.toLowerCase().trim().split(/\s+/).filter(term => term.length > 0)
    if (searchTerms.length === 0) return []

    const results: any[] = []

    searchIndex.forEach(({ item: obj, searchText, displayName }) => {
      if (matchesAnyTerm(searchText, searchTerms)) {
        const { score, matchedTerms } = getMatchScore(obj, searchTerms)

        results.push({
          ...obj,
          matchScore: score,
          matchedTerms,
          highlightedDisplayName: displayName,
          searchTerms
        })
      }
    })

    results.sort((a, b) => {
      if (a.matchScore !== b.matchScore) {
        return b.matchScore - a.matchScore
      }
      return b.matchedTerms - a.matchedTerms
    })

    return results.slice(0, 500)
  }, [debouncedSearch, searchIndex, matchesAnyTerm, getMatchScore])

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value)
  }, [])

  const handleSubmit = useCallback((e?: React.FormEvent) => {
    e?.preventDefault()
    setIsSubmit(true)
    setTimeout(() => setIsSubmit(false), 500)
  }, [])

  const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit()
    }
  }, [handleSubmit])

  const handleSearchClose = () => {
    setSearch('')
    handleClose()
  }

  const renderSearch = () => {
    const searchTermsCount = search.trim() ? search.trim().split(/\s+/).filter(term => term.length > 0).length : 0
    const isSearching = search !== debouncedSearch && search.trim() !== '' && search.trim()?.length > 2

    return (
      <Box mb={1}>
        <Stack direction={'row'} sx={{ padding: '0.5rem 0.8rem' }} alignItems={'center'}>
          <TextField
            ref={searchInputRef}
            size="small"
            autoFocus
            id="search"
            variant="outlined"
            value={search}
            disabled={isLoading || patientDetailStatus === 'loading'}
            className={classes.searchFieldBg}
            sx={{
              padding: "0px",
              margin: '2px 0px',
              width: '12rem',
              '& .MuiInputBase-root ': { height: '1.45rem !important' }
            }}
            onChange={handleSearchChange}
            onKeyUp={handleKeyPress}
            placeholder={searchTermsCount > 1 ? `Search (${searchTermsCount} terms - AND)` : "Search"}
            InputProps={{
              inputProps: { style: { padding: '0 0 0 5px', fontSize: '0.7rem' } },
              classes: { adornedEnd: classes.adornedEnd }
            }}
          />
          <Button
            className={`${classes.taskAddBtn} ${classes.medSubSelectActiveBtn}`}
            onClick={() => search.trim()?.length > 2 && handleSubmit()}
            disabled={!search.trim() || isSubmit || patientDetailStatus === 'loading'}
            variant="outlined"
            size="small"
          >
            <Typography className={classes.desc} variant="body2">
              {isSubmit ? 'Searching...' : 'Submit'}
            </Typography>
          </Button>
          {search && (
            <Typography variant='body1' className={classes.desc} color={'gray'} sx={{ ml: 1.8, pt: 0.2 }}>
              {isSearching ? (
                <><CircularProgress size={12} /> Searching...</>
              ) : (
                <>
                  {filteredSearchResults.length} result{filteredSearchResults.length !== 1 ? 's' : ''}
                  {searchTermsCount > 1 && ` (AND search)`}
                </>
              )}
            </Typography>
          )}
        </Stack>
        <Typography variant='body2' className={classes.desc} pl={2.2} mt={-1}>
          <i><small>Please enter at least 3 characters</small></i>
        </Typography>
      </Box>
    )
  }

  const getProbability = (probability: any) => {
    if (!probability) return '';

    const displayValue = probability < 0.1 ? '<0.1' : probability;
    return (
      <Tooltip title={<small>{`Probability ${probability}%`}</small>}>
        <Typography component="span" pl={0.2} color="textSecondary" variant="subtitle2" className={classes.subHeaderTitle}>
          <small>{`(${displayValue}%)`}</small>
        </Typography>
      </Tooltip>
    );
  }

  const renderPriority = (baselineIndex: number, priority: number, urgency: number) => {
    return <GradientCircleComponent
      baselineIndex={baselineIndex}
      priority={priority}
      urgency={parseInt(String(urgency || 0))}
    />
  }

  // function to get the changed conditions
  const getChangedConditions = useCallback(() => {
    if (!input?.recommendedConditions || !Array.isArray(input.recommendedConditions)) {
      return addConditions
    }

    // Create maps for quick lookup
    const changedConditions = [] as any
    const recommendedMap = new Map(
      input.recommendedConditions.map((rc: any) => [
        rc.condition_id,
        rc.selected_for_evaluation
      ])
    )

    const currentMap = new Map(
      addConditions.map((ac: any) => [
        ac.condition_id,
        ac.selected_for_evaluation
      ])
    )

    // Check for modified or added conditions
    for (const condition of addConditions) {
      const originalValue = recommendedMap.get(condition.condition_id)
      if (originalValue !== condition.selected_for_evaluation) {
        changedConditions.push(condition)
      }
    }

    // Check for removed conditions (originally selected but now unchecked)
    for (const originalCondition of input.recommendedConditions) {
      if (originalCondition.selected_for_evaluation === true) {
        const currentValue = currentMap.get(originalCondition.condition_id)
        if (currentValue === undefined || currentValue === false) {
          changedConditions.push({
            ...originalCondition,
            selected_for_evaluation: false
          })
        }
      }
    }

    return changedConditions
  }, [addConditions, input?.recommendedConditions])

  const handleUpdate = () => {
    const updatedConditions = getChangedConditions()

    if (updatedConditions?.length > 0) {
      setIsLoading(true)
      const problemId = input?.problem_id
      const updatedConditionList = updatedConditions?.map((item: any) => ({
        condition_id: item.condition_id,
        selected_for_evaluation: item.selected_for_evaluation,
      }))

      problemId && updatedConditionList?.length > 0 &&
        patientService.postConditionsEval(
          input.patient_id, problemId, { problem_conditions: updatedConditionList }
        ).then((response: any) => {
          handleSearchClose()
          const res = response?.data?.problem_conditions || []

          if (res?.length > 0) {
            setSearch('')
            handleClose(response?.data?.problem_conditions)
          }
          setIsLoading(false)
          updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true })
        }).catch((error: any) => {
          setIsLoading(false)
          toast.error(Helper.getErrorText(error), Helper.bottom_center())
          handleSearchClose()
        })
    } else {
      toast.warning('No data change to update!', Helper.bottom_center())
      return
    }
  }

  const handleConditionEval = (checked: boolean, condition: any) => {
    const isConditionExists = addConditions.some((i: any) => i.condition_id === condition.condition_id)
    let updatedConditionList = [] as any

    if (checked) {
      updatedConditionList = isConditionExists
        ? addConditions?.map((item: any) =>
          item.condition_id === condition.condition_id
            ? { ...item, selected_for_evaluation: checked }
            : item
        )
        : [...addConditions, { ...condition, selected_for_evaluation: checked }]
    } else {
      if (isConditionExists)
        updatedConditionList = addConditions?.filter((i: any) => i.condition_id !== condition.condition_id)
    }
    setAddConditions(updatedConditionList)
  }

  const getResultsToDisplay = useCallback((isRecommended: boolean) => {
    const isActiveSearch = debouncedSearch.trim().length >= 3

    let resultsToShow: any[] = []

    if (isRecommended) {
      resultsToShow = [...recommendedConditions]
    } else {
      if (isActiveSearch) {
        resultsToShow = [...filteredSearchResults]
      } else {
        resultsToShow = item && Array.isArray(item) ? [...item] : []
      }
    }

    return resultsToShow
  }, [debouncedSearch, filteredSearchResults, item, recommendedConditions])

  const renderResults = (isRecommended: boolean) => {
    const isActiveSearch = debouncedSearch.trim().length >= 3
    const searchTerms = isActiveSearch
      ? debouncedSearch.toLowerCase().trim().split(/\s+/).filter(term => term.length > 0)
      : []

    const resultsToShow = getResultsToDisplay(isRecommended)

    if (resultsToShow?.length < 1) {
      return (
        <Typography variant="body2" className={classes.desc} pl={1}>
          No conditions found
        </Typography>
      )
    }

    const sortedResults = [...resultsToShow].sort((a, b) => {
      const nameA = (a?.condition_name?.trim() || '').toLowerCase()
      const nameB = (b?.condition_name?.trim() || '').toLowerCase()
      return nameB < nameA ? 1 : -1
    })

    return (
      <Box sx={{ padding: '0.5rem 0rem 0rem 0rem' }}>
        {sortedResults?.map((d: any, index: number) => {
          return (
            <Box pb={0.1} className={classes.customMiniCard} key={`${d.id || d.condition_id || index}-${debouncedSearch}`}>
              <Card variant="outlined" className={classes.compactCard}>
                <CardContent className={classes.zeroPadding}>
                  <Stack direction='row' justifyContent="center" alignItems={'center'} spacing={0}>
                    <Checkbox
                      size="small"
                      className={classes.zeroPadding}
                      sx={{ padding: '0rem !important', transform: "scale(0.55)", height: '0.9rem' }}
                      name='condition_evaluation'
                      checked={addConditions?.some((item: any) => item.condition_id === d.condition_id && item.selected_for_evaluation !== false)}
                      onChange={(event: any, checked: any) => handleConditionEval(checked, d)}
                    />
                    <CardLayoutComponent
                      id={d.id}
                      name={d?.condition_id}
                      leftHeader={highlightMultipleTerms(d.condition_name, searchTerms)}
                      rightHeader={''}
                      subHeader={getProbability(d.probability)}
                      bottomText={renderPriority(d?.baseline_index || 0, d?.priority || 0, d?.urgency || 0)}
                      input={{
                        dependent_questionnaires: d?.dependent_questionnaires || [],
                        tests_per_cond: d?.tests_per_cond || [],
                        threshold: Object?.keys(d?.current_rationale) || [],
                        urgency: (d.urgency || 0),
                        synopsis: d?.synopsis || ''
                      }}
                      needs_confirmation={d.needs_confirmation || undefined}
                      needs_update={d.needs_update || false}
                      icons={[{ iconType: "more", callback: {} }]}
                      programs={d.programs || []}
                      onRefClick={() => null}
                      is_referred={d.is_referred}
                      is_overridden={d.is_overridden || undefined}
                      entityRef="problem_conditions"
                      patientId={input.patient_id}
                      category={'conditions'}
                    />
                  </Stack>
                </CardContent>
              </Card>
            </Box>
          )
        })}
      </Box>
    )
  }

  return (
    <Draggable ref={searchCalRef} defaultPosition={{ x: 80, y: 20 }} handle='.test-status-dialog'>
      <Dialog
        id={`test-status-id`}
        fullWidth={true}
        maxWidth="md"
        open={open}
        onClose={handleSearchClose}
        hideBackdrop
        disableScrollLock
        sx={{ position: "absolute", left: '10rem', top: '12%' }}
      >
        <DialogTitle className={classes.questionHeaderSection} classes={{ root: 'test-status-dialog' }}>
          <DialogTitleHeaderComponent
            title={input.title}
            handleDialogClose={handleSearchClose}
          />
        </DialogTitle>

        {(isLoading || patientDetailStatus === 'loading') && <LinearProgress />}

        <DialogContent
          className={classes.contextualHelpHeader}
          sx={{
            padding: '0.3rem 0rem 0.5rem 0.5rem !important',
            backgroundColor: 'var(--color-body-background) !important',
            height: '50vh',
            overflow: 'auto'
          }}
        >
          <Box>
            {renderSearch()}

            <Divider sx={{ margin: '0rem 1rem 0.5rem 0.5rem' }} />

            <Stack direction={'row'} spacing={2} pl={2} justifyContent={'flex-start'} alignItems={'baseline'}>
              {recommendedConditions?.length > 0 && (
                <Box width={'45%'}>
                  <Typography variant="body2" className={classes.desc} pl={0.5}>
                    <strong>Current pursued conditions</strong>
                  </Typography>
                  {renderResults(true)}
                </Box>
              )}
              <Box width={'45%'}>
                <Typography variant="body2" className={classes.desc} pl={0.5}>
                  <strong>{(debouncedSearch.trim().length >= 3) ? 'Search results' : 'All conditions'}</strong>
                </Typography>
                {renderResults(false)}
              </Box>
            </Stack>
          </Box>
        </DialogContent>

        <DialogActions className={classes.btnContainer} sx={{ backgroundColor: 'var(--color-paper) !important', margin: '0.4rem !important' }}>
          <Button
            disabled={isLoading || !hasDataChanged()}
            onClick={() => handleUpdate()}
            variant="outlined"
            size='small'
          >
            <strong>Update</strong>
          </Button>
          <Button onClick={handleSearchClose} variant="outlined" size='small'>
            <strong>Cancel</strong>
          </Button>
        </DialogActions>
      </Dialog>
    </Draggable>
  )
};

export default withStyles(stylesheet)(SearchConditionsComponent)